import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AppUserService } from 'src/app/services/app-user.service';
import { AppSelectionService } from 'src/app/services/app-selection.service';
import { UserAppResponse } from 'src/app/services/models/app-user.model';

/**
 * Lets a user choose between applications. Reached from /loading when there is
 * more than one, and from the top bar's "Switch App" button.
 *
 * In both cases the app list is already in the store, so getUserAppList()
 * resolves without a round trip. The loading and auto-forward branches below
 * only matter for a cold deep-link straight to this URL.
 */
@Component({
  selector: 'bhub-app-picker',
  templateUrl: './app-picker.component.html',
  styleUrls: ['./app-picker.component.scss'],
  standalone: false
})
export class AppPickerComponent implements OnInit, OnDestroy {
  apps: UserAppResponse[] = [];
  loading = true;
  error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private appUserService: AppUserService,
    private appSelection: AppSelectionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.appUserService.getUserAppList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: apps => {
          this.apps = apps || [];
          this.loading = false;

          if (this.apps.length === 0) {
            this.router.navigate(['/no-access'], { replaceUrl: true });
          } else if (this.apps.length === 1) {
            this.appSelection.selectApplication(this.apps[0].applicationId);
          }
        },
        error: () => {
          this.loading = false;
          this.error = 'Unable to load your applications. Please try again.';
        }
      });
  }

  select(app: UserAppResponse): void {
    this.appSelection.selectApplication(app.applicationId);
  }

  displayName(app: UserAppResponse): string {
    return app.applicationName || `Application ${app.applicationId}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
